/**
 * Box 9 of the ICS 215A-CG, the single Prepared by.
 *
 * The NIMS 215A the descriptor is read off splits box 8 into a Safety Officer
 * and an Operations Section Chief; the Coast Guard sheet prints one generic
 * box, so the label comes from `ICS215A_BOX_LABELS` rather than the descriptor.
 * Signing happens on the version bar above, as on every versioned form.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
} from './AssignmentList/ics204SignatureSlots';
import { ICS215A_BOX_LABELS } from './ics215aBoxLabels';

export type Ics215ASignatureBoxProps = {
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

export const Ics215ASignatureBox: React.FC<Ics215ASignatureBoxProps> = ({
  periodNumber,
}) => {
  const { data: tip } = useFormVersionTip('ICS_215A', periodNumber);
  const signature = useMemo(
    () => parseWalaSignatures(tip?.signatures)[0],
    [tip?.signatures]
  );

  return (
    <Ics204CardSection
      boxLabel={ICS215A_BOX_LABELS.preparedBy}
      testId="ics215a-signature-box"
      className="tw:mt-0"
    >
      {signature ? (
        <div className="tw:grid tw:gap-1 tw:text-sm">
          <Line label="Name" value={signature.name} />
          <Line
            label="Position / Title"
            // The roster seat the signer held, resolved by the server. Older
            // signatures predate it and fall back to the slot they filled.
            value={signature.positionTitle || signature.role}
          />
          <Line label="Signature" value={signature.name} italic />
          <Line
            label="Date / Time"
            value={formatSignatureDateTime(signature.signedAt)}
          />
        </div>
      ) : (
        <div className="tw:text-sm tw:text-muted-foreground">
          Not signed. Create a signed version above to sign this form.
        </div>
      )}
    </Ics204CardSection>
  );
};

export default Ics215ASignatureBox;
